import React from "react";
import { AfterContent } from "../components/jsonBlocks/AfterContent/AfterContent";
import { InRead } from "../components/jsonBlocks/InRead/InRead";
import { TextBlock } from "../components/jsonBlocks/TextBlock/TextBlock";
import { ReadAlso } from "../components/jsonBlocks/TextBlock/ReadAlso";
import { AdfoxSticky } from "../components/adfoxBlocks/AdfoxSticky";
import { InImageAd } from "../components/YandexAd/InImageAd";
import { AdvManager } from "../components/YandexAd/AdvManager";
import { SourceInfo } from "../components/SourceInfo/SourceInfo";
import { splitNewsContent } from "../utils/adChunks";
import { AdfoxBlock } from "../components/adfoxBlocks/AdfoxBlock";
import { AdSlot } from "../components/ads/AdSlot";

function getAdfoxComponents(isFull, isDesktop, options) {
    return {
        1: options ? (
            <AdfoxSticky isFull={isFull} isDesktop={isDesktop} options={options.ir1} className="adfox-ir" />
        ) : null,
        2: options ? (
            <AdfoxSticky isFull={isFull} isDesktop={isDesktop} options={options.ir2} className="adfox-ir" />
        ) : null,
        3: options ? (
            <AdfoxSticky isFull={isFull} isDesktop={isDesktop} options={options.ir3} className="adfox-ir" />
        ) : null,
        4: options ? (
            <AdfoxSticky isFull={isFull} isDesktop={isDesktop} options={options.ir4} className="adfox-ir" />
        ) : null,
    };
}

function getYandexComponents(isFull, isDesktop, options) {
    return {
        1: options ? <AdvManager isFull={isFull} isDesktop={isDesktop} options={options.ir1} /> : null,
        2: options ? <AdvManager isFull={isFull} isDesktop={isDesktop} options={options.ir2} /> : null,
        3: options ? <AdvManager isFull={isFull} isDesktop={isDesktop} options={options.ir3} /> : null,
        4: options ? <AdvManager isFull={isFull} isDesktop={isDesktop} options={options.ir4} /> : null,
    };
}

/**
 * ir-слоты: у перенесённых кампаний провайдер берётся из БД полем, у остальных —
 * из списка версий, как было.
 */
function getIrComponents(isFull, isDesktop, options, fallbackMap, jsonMap) {
    const slots = { 1: "ir1", 2: "ir2", 3: "ir3", 4: "ir4" };
    const map = {};

    for (const [position, slot] of Object.entries(slots)) {
        map[position] = (
            <AdSlot
                slot={slot}
                isFull={isFull}
                isDesktop={isDesktop}
                className="adfox-ir"
                fallback={fallbackMap[position]}
                json={jsonMap ? jsonMap[position] : undefined}
            />
        );
    }

    return map;
}

/**
 * Фолбэк для версий `main` и `48306`: на первом месте adfox-блок, дальше — срезы ленты.
 *
 * Раньше эти версии рендерились здесь напрямую, мимо `AdSlot`, поэтому конфиг из
 * админки до них не доходил: `?fpr=48306-new` без хвоста версии Core приводит к `main`,
 * и запрос попадал ровно в эту ветку. Теперь тот же JSX уходит в `fallback`, а слот
 * сначала спрашивает БД — как на остальных версиях шаблона.
 */
function getMainFallbacks(isFull, isDesktop, options, jsonData, objectId, showId) {
    const map = {
        1: options ? (
            <AdfoxBlock isFull={isFull} isDesktop={isDesktop} options={options.ir1} className="adfox-ir" />
        ) : null,
    };

    // Срез ленты повторяет прежний расчёт: позиция 2 брала slice(1, 2), и так далее.
    for (const position of [2, 3, 4]) {
        map[position] = (
            <InRead jsonData={jsonData.slice(position - 1, position)} objectId={objectId} showId={showId} />
        );
    }

    return map;
}

/**
 * Фолбэк для версий с лентой вместо ir-блоков (`showJsonIr`, версии `2` и `3`):
 * на втором месте текстовый блок, на остальных — срезы ленты.
 */
function getJsonIrFallbacks(jsonData, objectId, showId) {
    const map = {};

    for (const position of [1, 2, 3, 4]) {
        map[position] =
            position === 2 ? (
                <TextBlock
                    jsonData={jsonData.slice(13, 16)}
                    withBackground={true}
                    showBorders={true}
                    useMargin={true}
                    objectId={objectId}
                    showId={showId}
                />
            ) : (
                <InRead jsonData={jsonData.slice(position - 1, position)} objectId={objectId} showId={showId} />
            );
    }

    return map;
}

function NewsText({
    news,
    jsonData,
    isFull,
    isDesktop,
    showAdfox,
    options,
    objectId,
    onReload,
    showId,
    fullNewsUrl,
    fprVersion,
    showJson,
    showJsonIr,
    dbMode = false,
}) {
    const [isReloaded, setIsReloaded] = React.useState(false);
    const contentRef = React.useRef(null);
    const chunks = splitNewsContent(news, 4, 3);
    const adfoxNum = chunks.filter((c) => c === "ADFOX").length;
    const adfoxComponentsMap = getAdfoxComponents(isFull, isDesktop, options);
    const yandexComponentsMap = getYandexComponents(isFull, isDesktop, options);
    const fallbackComponentsMap = ["4", "66972"].includes(fprVersion) ? yandexComponentsMap : adfoxComponentsMap;
    const jsonIrFallbacks = getJsonIrFallbacks(jsonData, objectId, showId);
    const selectedComponentsMap = getIrComponents(isFull, isDesktop, options, fallbackComponentsMap);
    const jsonIrComponentsMap = getIrComponents(isFull, isDesktop, options, jsonIrFallbacks);
    const mainComponentsMap = getIrComponents(
        isFull,
        isDesktop,
        options,
        getMainFallbacks(isFull, isDesktop, options, jsonData, objectId, showId),
    );
    // В режиме БД путь один — слот ir{n}: какую из карт выше брать, решали списки версий,
    // а json-места заполняются тем же, чем и в ленточных версиях.
    const dbComponentsMap = getIrComponents(isFull, isDesktop, options, {}, jsonIrFallbacks);
    let cnt = 1;

    const handleClick = () => {
        setIsReloaded(true);
        onReload && onReload(true);
    };

    return (
        <div className="newsDetail">
            {!dbMode && <InImageAd blockId={options.inImageAd} />}
            <SourceInfo url={news.source_url} title={news.source_title} />
            <div className={!isFull ? "news-content-inner__toggle" : null} ref={contentRef}>
                {chunks.map((chunk, i) => {
                    if (chunk === "ADFOX" && cnt <= adfoxNum + 1 && isFull) {
                        cnt += 1;
                        if (dbMode) {
                            return <React.Fragment key={`ad-${cnt - 1}`}>{dbComponentsMap[cnt - 1]}</React.Fragment>;
                        }

                        if (["main", "48306"].includes(fprVersion)) {
                            return <React.Fragment key={`ad-${cnt - 1}`}>{mainComponentsMap[cnt - 1]}</React.Fragment>;
                        }

                        if (!showJsonIr) {
                            return (
                                <React.Fragment key={`ad-${cnt - 1}`}>{selectedComponentsMap[cnt - 1]}</React.Fragment>
                            );
                        }

                        return <React.Fragment key={`ad-${cnt - 1}`}>{jsonIrComponentsMap[cnt - 1]}</React.Fragment>;
                    }
                    if (i === chunks.length - 1) {
                        cnt = 0;
                    }
                    return (
                        <React.Fragment key={i}>
                            <p hidden={!isFull && chunk === "ADFOX"} dangerouslySetInnerHTML={{ __html: chunk }} />
                        </React.Fragment>
                    );
                })}
            </div>
            <div className={!isFull ? "toggleNews" : "hidden"}>
                <div className={!isFull ? "fadeNews" : null}></div>
                <a
                    className={!isFull ? "openButton" : "hidden"}
                    href={fullNewsUrl.toString()}
                    target="_blank"
                    onClick={handleClick}
                >
                    Показать больше
                </a>
            </div>
            {/* Тизерные блоки без слота: в режиме БД — только при ленте из БД. */}
            {(dbMode ? Boolean(objectId) : showJson) && (
                <ReadAlso jsonData={jsonData.slice(8, 13)} showDigits={true} showReadAlso={true} objectId={objectId} />
            )}
            {(dbMode ? Boolean(objectId) : showJson) && (
                <AfterContent jsonData={jsonData.slice(5, 8)} objectId={objectId} />
            )}
        </div>
    );
}

NewsText.defaultProps = {
    teasers: [],
};

export default NewsText;
